import * as actionTypes from "../constants";
import * as apis from "../requests/apis";
import * as payloads from "../requests/payloads";
import { clientRequest } from "../requests";

// Get Reports
// Score/GetUserScores wants DateRangeRequest {userId, fromDate, toDate} —
// callers already pass a userId here despite the old `email` param name
// (e.g. dashboard/index.js calls getReports(profile?.id)).
export const getReports = (userId, dates) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FETCH_REPORTS });
    const res = await clientRequest(
      actionTypes.POST,
      apis.GET_REPORTS_API,
      { userId: userId || null, fromDate: dates.fromDate, toDate: dates.toDate }
    );
    dispatch({
      type: actionTypes.GET_REPORT_SUCCESS,
      payload: res.data,
    });
  } catch (err) {
    dispatch({
      type: actionTypes.GET_REPORT_FAIL,
      payload: err,
    });
  }
};

// Get module Reports
export const getModuleReports = (userId, dates) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FETCH_REPORTS });
    const res = await clientRequest(
      actionTypes.POST,
      apis.GET_MODULE_PLAY_REPORTS_API,
      { userId, fromDate: dates.fromDate, toDate: dates.toDate }
    );
    dispatch({
      type: actionTypes.GET_MODULE_REPORT_SUCCESS,
      payload: res.data,
    });
  } catch (err) {
    dispatch({
      type: actionTypes.GET_MODULE_REPORT_FAIL,
      payload: err,
    });
  }
};

// Get play Reports
export const getPlayReports = (userId, dates) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FETCH_PLAY_REPORTS });
    const res = await clientRequest(
      actionTypes.POST,
      apis.GET_PLAY_REPORTS_API,
      { userId, fromDate: dates.fromDate, toDate: dates.toDate }
    );
    dispatch({
      type: actionTypes.GET_PLAY_REPORT_SUCCESS,
      payload: res.data,
    });
  } catch (err) {
    dispatch({
      type: actionTypes.GET_PLAY_REPORT_FAIL,
      payload: err,
    });
  }
};

// Get Cognitive Fitness Score (4-level: FitnessScore, CategoryBreakdown, RollupScores)
export const getCognitiveFitnessScore = (userId, dates) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FETCH_COGNITIVE_FITNESS });
    const res = await clientRequest(
      actionTypes.POST,
      apis.GET_COGNITIVE_FITNESS_API,
      { userId, fromDate: dates.fromDate, toDate: dates.toDate }
    );
    dispatch({
      type: actionTypes.GET_COGNITIVE_FITNESS_SUCCESS,
      payload: res.data,
    });
  } catch (err) {
    dispatch({
      type: actionTypes.GET_COGNITIVE_FITNESS_FAIL,
      payload: err,
    });
  }
};

// Get Session Summary (optional fromDate/toDate make category deltas date-aware)
export const getSessionSummary = (userId, dates) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FETCH_SESSION_SUMMARY });
    const res = await clientRequest(
      actionTypes.POST,
      apis.GET_SESSION_SUMMARY_API,
      { userId, fromDate: dates?.fromDate || null, toDate: dates?.toDate || null }
    );
    dispatch({
      type: actionTypes.GET_SESSION_SUMMARY_SUCCESS,
      payload: res.data,
    });
  } catch (err) {
    dispatch({
      type: actionTypes.GET_SESSION_SUMMARY_FAIL,
      payload: err,
    });
  }
};

// Get Exercise Activity by userId
export const getExerciseActivity = (userId, dates) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FETCH_EXERCISE_ACTIVITY });
    const res = await clientRequest(
      actionTypes.POST,
      apis.GET_EXERCISE_ACTIVITY_API,
      { userId, fromDate: dates.fromDate, toDate: dates.toDate }
    );
    dispatch({ type: actionTypes.GET_EXERCISE_ACTIVITY_SUCCESS, payload: res.data });
  } catch (err) {
    dispatch({ type: actionTypes.GET_EXERCISE_ACTIVITY_FAIL, payload: err });
  }
};

// Get Meditation Activity by userId
export const getMeditationActivity = (userId, dates) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FETCH_MEDITATION_ACTIVITY });
    const res = await clientRequest(
      actionTypes.POST,
      apis.GET_MEDITATION_ACTIVITY_API,
      { userId, fromDate: dates.fromDate, toDate: dates.toDate }
    );
    dispatch({ type: actionTypes.GET_MEDITATION_ACTIVITY_SUCCESS, payload: res.data });
  } catch (err) {
    dispatch({ type: actionTypes.GET_MEDITATION_ACTIVITY_FAIL, payload: err });
  }
};

// Get Play Report by userId
export const getPlayReportById = (userId, dates) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FETCH_PLAY_REPORT_BY_ID });
    const res = await clientRequest(
      actionTypes.POST,
      apis.GET_PLAY_REPORT_BY_ID_API,
      { userId, fromDate: dates.fromDate, toDate: dates.toDate }
    );
    dispatch({ type: actionTypes.GET_PLAY_REPORT_BY_ID_SUCCESS, payload: res.data });
  } catch (err) {
    dispatch({ type: actionTypes.GET_PLAY_REPORT_BY_ID_FAIL, payload: err });
  }
};

// Get Module Play Report by userId
export const getModulePlayReportById = (userId, dates) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FETCH_MODULE_PLAY_REPORT_BY_ID });
    const res = await clientRequest(
      actionTypes.POST,
      apis.GET_MODULE_PLAY_REPORT_BY_ID_API,
      { userId, fromDate: dates.fromDate, toDate: dates.toDate }
    );
    dispatch({ type: actionTypes.GET_MODULE_PLAY_REPORT_BY_ID_SUCCESS, payload: res.data });
  } catch (err) {
    dispatch({ type: actionTypes.GET_MODULE_PLAY_REPORT_BY_ID_FAIL, payload: err });
  }
};

// Get pre-aggregated activity summary (daily | weekly | monthly)
// Pass fromDate/toDate (yyyy-MM-DD) for exact ranges — bypasses cache and computes live
export const getActivitySummary = (userId, period, fromDate, toDate) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FETCH_ACTIVITY_SUMMARY });
    const res = await clientRequest(
      actionTypes.POST,
      apis.GET_ACTIVITY_SUMMARY_API,
      { userId, period, fromDate: fromDate || null, toDate: toDate || null }
    );
    dispatch({ type: actionTypes.GET_ACTIVITY_SUMMARY_SUCCESS, payload: res.data });
  } catch (err) {
    dispatch({ type: actionTypes.GET_ACTIVITY_SUMMARY_FAIL, payload: err });
  }
};

// Get Behavioural Report (daily sleep/food/mood/activity scores from game play)
export const getBehaviouralReport = (userId, dates) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.FETCH_BEHAVIOURAL_REPORT });
    const res = await clientRequest(
      actionTypes.POST,
      apis.GET_BEHAVIOURAL_REPORT_API,
      { userId, fromDate: dates.fromDate, toDate: dates.toDate }
    );
    dispatch({
      type: actionTypes.GET_BEHAVIOURAL_REPORT_SUCCESS,
      payload: res.data,
    });
  } catch (err) {
    dispatch({
      type: actionTypes.GET_BEHAVIOURAL_REPORT_FAIL,
      payload: err,
    });
  }
};
